"use client"
import { IndustryCard } from "./IndusryCard";
import { Button } from "@/components/ui/button";
import { fontWeights } from "@/utils/fonts";
import { useState } from "react";

export function IndustryExpertise({
    title,
    description,
    items,
    locationName,
    serviceName
}: {
    title?: string;
    description?: string;
    items?: any[];
    locationName?: string;
    serviceName?: string;
}) {
    const [showAll, setShowAll] = useState(false);

    const replaceDynamicPlaceholders = (text?: string) => {
        if (!text) return text;
        return text
            .replace(/{location}/g, locationName || "")
            .replace(/{service}/g, serviceName || "");
    };

    const finalTitle = replaceDynamicPlaceholders(title);
    const finalDescription = replaceDynamicPlaceholders(description);

    const displayIndustries = (items || []).map((item: any) => ({
        ...item,
        title: replaceDynamicPlaceholders(item.title),
        description: replaceDynamicPlaceholders(item.description),
        iconWidth: item.iconWidth || '48',
        iconHeight: item.iconHeight || '48'
    }));

    if (displayIndustries.length === 0 && !finalTitle && !finalDescription) {
        return null;
    }

    const visibleIndustries = showAll ? displayIndustries : displayIndustries.slice(0, 5);
    const firstRow = visibleIndustries.slice(0, 3);
    const secondRow = visibleIndustries.slice(3, 5);
    const thirdRow = visibleIndustries.slice(5, 8);
    const fourthRow = visibleIndustries.slice(8, 10);
    const fifthRow = visibleIndustries.slice(10);
    const clickHandler = () => {
        setShowAll(!showAll);
    };


    return (
        <section className=" bg-[#002F6D]/5">
            <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[30px] 2xl:px-[100px] pt-2 sm:pt-4 lg:pt-10 pb-5">
                <div className="text-center lg:mb-[50px] mb-6">
                    <h2
                        className="
                            text-[24px] 
                            lg:text-[35px] 
                            2xl:text-[40px] 
                            3xl:text-[45px] 
                            mb-3
                            lg:mb-6
                            2xl:mb-[50px]
                            mx-auto
                            xl:w-[362]
                            w-auto
                            3xl:w-[380px]
                            max-w-[380px]
                            h-auto
                            3xl:h-[54px]
                            max-h-auto
                            font-lato
                            text-[#000000]
                            "
                        style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.semiBold }}
                    >
                        {finalTitle && <span dangerouslySetInnerHTML={{ __html: finalTitle }} />}
                    </h2>
                    {finalDescription && (
                        <p
                            className="
                                text-[#000000] 
                                mx-auto
                                text-[14px] 
                                lg:text-[17px] 
                                2xl:text-[18px]
                                max-w-[1186px]
                                h-auto
                                px-0
                                sm:px-10
                                md:px-20
                                "
                            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                        >
                            {finalDescription && <span dangerouslySetInnerHTML={{ __html: finalDescription }} />}
                        </p>
                    )}
                </div>
                <div className="flex flex-col 2xl:gap-[30px] lg:gap-[20px] gap-[10px] mx-auto gap-x-[10px] max-w-[1220px]">
                    {firstRow.length > 0 && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 mx-auto gap-[10px]">
                            {firstRow.map((industry, i) => (
                                <IndustryCard key={i} title={industry.title} description={industry.description} icon={industry.icon} iconHeight={industry.iconHeight} iconWidth={industry.iconWidth} />
                            ))}
                        </div>
                    )}
                    {secondRow.length > 0 && (
                        <div className="mx-auto">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px] ">
                                {secondRow.map((industry, i) => (
                                    <IndustryCard key={i} title={industry.title} description={industry.description} icon={industry.icon} iconHeight={industry.iconHeight} iconWidth={industry.iconWidth} />
                                ))}
                            </div>
                        </div>
                    )}
                    {thirdRow.length > 0 && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 mx-auto gap-[10px]">
                            {thirdRow.map((industry, i) => (
                                <IndustryCard key={i} title={industry.title} description={industry.description} icon={industry.icon} iconHeight={industry.iconHeight} iconWidth={industry.iconWidth} />
                            ))}
                        </div>
                    )}
                    {fourthRow.length > 0 && (
                        <div className="mx-auto">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px] ">
                                {fourthRow.map((industry, i) => (
                                    <IndustryCard key={i} title={industry.title} description={industry.description} icon={industry.icon} iconHeight={industry.iconHeight} iconWidth={industry.iconWidth} />
                                ))}
                            </div>
                        </div>
                    )}
                    {fifthRow.length > 0 && (
                        <div className={`grid grid-cols-1 lg:grid-cols-3 mx-auto gap-[10px] ${showAll ? "2xl:mb-[70px] lg:mb-[40px] mb-[30px]" : "mb-0"} `}>
                            {fifthRow.map((industry, i) => (
                                <IndustryCard key={i} title={industry.title} description={industry.description} icon={industry.icon} iconHeight={industry.iconHeight} iconWidth={industry.iconWidth} />
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex flex-col items-center justify-center mx-auto">
                    {!showAll && displayIndustries.length > 5 && (
                        <Button
                            variant="outline"
                            aria-label="See more industries"
                            className="
                                bg-[#002F6D]
                                text-white 
                                hover:bg-[#002F6D]/90 
                                w-[110px]
                                h-[40px]
                                lg:w-[150px] 
                                lg:h-[50px]
                                rounded-[8px]
                                mt-8
                                sm:mt-12
                                lg:mt-16
                                xl:mt-20
                                sm:rounded-[10px]
                                text-[15px] 
                                3xl:text-[18px]
                                hover:text-orange-300
                                "
                            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                            onClick={clickHandler}
                        >
                            See More
                        </Button>
                    )}

                </div>
            </div>
            <div
                className="
                    pt-[40px]
                    sm:pt-[50px]
                    lg:pt-[60px]
                    xl:pt-[60px] 
                    2xl:pt-[70px]
                    pb-[5px]
                    tsm-350:pb-[20px] 
                    sm:pb-[40px] 
                    2xl:pb-[60px]
                    bg-white
                    "
            >
                <div className="lg:h-[1px] h-[0.95px] lg:w-[200px] w-[150px] bg-[#002F6D] mx-auto" />
            </div>
        </section>
    );
}
